import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRecordById, updateRecord } from './api/airtable';

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getRecordById(id);
      setRecord(data);
      setFormData(data.fields);
    })();
  }, [id]);

  const fmtDate = str => (!str ? '—' : `${new Date(str).getMonth() + 1}/${new Date(str).getDate()}/${new Date(str).getFullYear()}`);

  const addressString = useMemo(() => {
    if (!record) return '';
    const f = record.fields;
    return [
      f['Street Address'],
      f['Current City/Town'],
      f['Current State (or Province/Region)'],
      f['Current Country'],
      f['Current Zip']
    ]
      .filter(Boolean)
      .join(', ');
  }, [record]);

  const handleChange = (field, value) => setFormData(p => ({ ...p, [field]: value }));

  const save = async () => {
    setLoading(true);
    try {
      await updateRecord(id, formData);
      setRecord(prev => ({ ...prev, fields: formData }));
      setIsEditing(false);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!record) return <p className="mt-16 text-center">Loading…</p>;

  const f = formData;

  const prettyAI = raw =>
    raw?.split('\n').map((line, idx) => {
      const [label, ...rest] = line.split(':');
      return (
        <p key={idx} className="leading-snug">
          <span className="font-semibold text-[#8C1515]">{label.trim()}:</span>
          {rest.join(':')}
        </p>
      );
    });

  const displayRows = [
    { label: 'Address', value: addressString || '—' },
    { label: 'Gender Identification', value: f['Gender Identification'] || '—' },
    { label: 'Email', value: f['Email'] || '—' },
    { label: 'Cell Phone Number', value: f['Cell Phone Number'] || '—' },
    { label: 'Date of Birth', value: fmtDate(f['Date of Birth (MM/DD/YYYY)']) },
    { label: 'School / Job', value: f['Current Job or School'] || f['School / Job'] || '—' },
    // New MCAT row
    { label: 'MCAT', value: f['MCAT Taken'] ? `${f['MCAT Taken']}${f['MCAT Score/Notes'] ? `: ${f['MCAT Score/Notes']}` : ''}` : '—' },
    // New Research Experience row
    { label: 'Research Experience', value: f['Research Experience'] || '—' }
  ];

  const editFields = [
    'Full Name',
    'Other Names (e.g. Maiden Name)',
    'Street Address',
    'Current City/Town',
    'Current State (or Province/Region)',
    'Current Country',
    'Current Zip',
    'Gender Identification',
    'Email',
    'Cell Phone Number',
    'Date of Birth (MM/DD/YYYY)',
    'Current Job or School',
    // New editable fields
    'MCAT Taken',
    'MCAT Score/Notes',
    'Research Experience',
    'AI Update (Most Recent)',
    'Notes'
  ];

  return (
    <div className="relative mx-auto mt-12 max-w-xl rounded-lg border border-gray-300 bg-white p-6 shadow">
      <button onClick={() => navigate(-1)} className="mb-4 rounded bg-gray-300 px-3 py-1 font-semibold text-gray-800 hover:bg-gray-400">← Back</button>

      <h1 className="mb-1 text-3xl font-bold text-[#8C1515]">{f['Full Name'] || 'No Name'}</h1>
      {f['Other Names (e.g. Maiden Name)'] && !isEditing && <p className="mb-4 text-sm text-gray-600">({f['Other Names (e.g. Maiden Name)']})</p>}

      {/* normal mode */}
      {!isEditing && (
        <div className="space-y-3 text-gray-800">
          {displayRows.map(r => (
            <p key={r.label}>
              <span className="font-semibold">{r.label}:</span> {r.value}
            </p>
          ))}

          <div className="mt-4">
            <p className="mb-1 font-semibold">AI Update (Most Recent):</p>
            {f['AI Update (Most Recent)'] ? (
              <div className="ml-2 space-y-1 text-sm">{prettyAI(f['AI Update (Most Recent)'])}</div>
            ) : (
              <p className="ml-2 text-sm text-gray-500">—</p>
            )}
          </div>

          <div className="mt-4">
            <p className="font-semibold">Notes:</p>
            <p className="whitespace-pre-line text-sm">{f['Notes'] || '—'}</p>
          </div>
        </div>
      )}

      {/* edit mode */}
      {isEditing && (
        <div className="space-y-4">
          {editFields.map(field => (
            <div key={field}>
              <label className="mb-1 block font-semibold">{field}</label>
              <input
                type={field.includes('Date') ? 'date' : 'text'}
                value={f[field] || ''}
                onChange={e => handleChange(field, e.target.value)}
                className="w-full rounded border border-gray-300 p-2"
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 space-x-3">
        {isEditing ? (
          <>
            <button onClick={save} disabled={loading} className="rounded bg-[#8C1515] px-4 py-2 font-semibold text-white hover:bg-[#A6192E] disabled:opacity-50">
              {loading ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => { setFormData(record.fields); setIsEditing(false); }} className="rounded bg-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-400">
              Cancel
            </button>
          </>
        ) : (
          <button onClick={() => setIsEditing(true)} className="rounded bg-[#8C1515] px-4 py-2 font-semibold text-white hover:bg-[#A6192E]">
            Edit
          </button>
        )}
      </div>

      {showToast && <div className="absolute bottom-4 right-4 rounded bg-green-600 px-4 py-2 font-semibold text-white shadow-lg">✅ Record updated!</div>}
    </div>
  );
}
